/**
 * Sprint S13.1.B.2 — Step 5: active sports + equipment + training facilities.
 *
 * Three FieldMultiSelect groups, each с controlled vocab + free-text "other:"
 * prefix pattern (matches Step2/Step4 convention).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { FieldMultiSelect } from "@/components/onboarding/fields/FieldMultiSelect";
import { planningProfileIssues } from "@/lib/onboarding-readiness";


const SPORT_OPTIONS = [
  "running",
  "cycling",
  "swimming",
  "strength",
  "triathlon",
  "yoga",
  "hiking",
  "climbing",
  "rowing",
  "skiing",
];

const EQUIPMENT_OPTIONS = [
  "road_bike",
  "tt_bike",
  "turbo_trainer",
  "power_meter",
  "garmin_watch",
  "heart_rate_strap",
  "treadmill",
  "rowing_machine",
  "kettlebell",
  "barbell",
  "pull_up_bar",
  "trail_shoes",
];

const FACILITY_OPTIONS = [
  "pool",
  "open_water",
  "outdoor_track",
  "road",
  "trail",
  "home_gym",
  "commercial_gym",
];


function splitControlledOther(values: string[]): {
  controlled: string[];
  other: string;
} {
  const controlled: string[] = [];
  const otherParts: string[] = [];
  for (const v of values) {
    if (v.startsWith("other:")) otherParts.push(v.slice(6));
    else controlled.push(v);
  }
  return { controlled, other: otherParts.join(", ") };
}


export function Step5SportProfile() {
  const { t } = useTranslation();
  const data = useOnboardingStore((s) => s.data);
  const setField = useOnboardingStore((s) => s.setField);
  const nextStep = useOnboardingStore((s) => s.nextStep);
  const [sportError, setSportError] = useState<string | null>(null);

  const [otherSport, setOtherSport] = useState(
    splitControlledOther(data.active_sports).other,
  );
  const [otherEquipment, setOtherEquipment] = useState(
    splitControlledOther(data.equipment).other,
  );
  const [otherFacility, setOtherFacility] = useState(
    splitControlledOther(data.training_facilities).other,
  );

  const sportOptions = SPORT_OPTIONS.map((v) => ({
    value: v,
    label: t(`onboarding.step.sport_profile.sport.${v}`),
  }));
  const equipmentOptions = EQUIPMENT_OPTIONS.map((v) => ({
    value: v,
    label: t(`onboarding.step.sport_profile.equipment.${v}`),
  }));
  const facilityOptions = FACILITY_OPTIONS.map((v) => ({
    value: v,
    label: t(`onboarding.step.sport_profile.facility.${v}`),
  }));

  const controlledSports = data.active_sports.filter((v) => !v.startsWith("other:"));
  const controlledEquip = data.equipment.filter((v) => !v.startsWith("other:"));
  const controlledFac = data.training_facilities.filter((v) => !v.startsWith("other:"));
  const sportWarnings = planningProfileIssues({ ...data,
    active_sports: [...controlledSports, ...(otherSport.trim() ? [`other:${otherSport.trim()}`] : [])],
  }).filter(issue => issue.warning);

  const handleNext = () => {
    const sports = [...controlledSports];
    if (otherSport.trim()) sports.push(`other:${otherSport.trim()}`);
    setField("active_sports", sports);

    const equip = [...controlledEquip];
    if (otherEquipment.trim()) equip.push(`other:${otherEquipment.trim()}`);
    setField("equipment", equip);

    const fac = [...controlledFac];
    if (otherFacility.trim()) fac.push(`other:${otherFacility.trim()}`);
    setField("training_facilities", fac);
    const issue = planningProfileIssues({ ...data, active_sports: sports }).find(item => item.step === 4 && !item.warning);
    setSportError(issue?.key ?? null);
    if (issue) return;
    nextStep();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2
          id="step-heading"
          tabIndex={-1}
          className="mb-2 text-xl font-semibold text-navy-deep sm:text-2xl"
        >
          {t("onboarding.step.sport_profile.title")}
        </h2>
        <p className="text-[13px] text-taupe sm:text-[14px]">
          {t("onboarding.step.sport_profile.subtitle")}
        </p>
      </div>

      <FieldMultiSelect
        label={t("onboarding.step.sport_profile.sports_label")}
        hint={t("onboarding.step.sport_profile.sports_hint")}
        options={sportOptions}
        selected={controlledSports}
        onChange={(values) => setField("active_sports", values)}
        allowOther
        otherValue={otherSport}
        onOtherChange={setOtherSport}
        otherPlaceholder={t("onboarding.step.sport_profile.other_sport_placeholder")}
      />

      <FieldMultiSelect
        label={t("onboarding.step.sport_profile.equipment_label")}
        hint={t("onboarding.step.sport_profile.equipment_hint")}
        options={equipmentOptions}
        selected={controlledEquip}
        onChange={(values) => setField("equipment", values)}
        allowOther
        otherValue={otherEquipment}
        onOtherChange={setOtherEquipment}
        otherPlaceholder={t("onboarding.step.sport_profile.other_equipment_placeholder")}
      />

      <FieldMultiSelect
        label={t("onboarding.step.sport_profile.facilities_label")}
        hint={t("onboarding.step.sport_profile.facilities_hint")}
        options={facilityOptions}
        selected={controlledFac}
        onChange={(values) => setField("training_facilities", values)}
        allowOther
        otherValue={otherFacility}
        onOtherChange={setOtherFacility}
        otherPlaceholder={t("onboarding.step.sport_profile.other_facility_placeholder")}
      />

      <div className="pt-2">
        {sportWarnings.map(issue => <p key={issue.key} role="note" className="mb-3 text-sm text-taupe">{t(issue.key)}</p>)}
        {sportError && <p role="alert" className="mb-3 text-sm text-coral">{t(sportError)}</p>}
        <Button
          type="button"
          onClick={handleNext}
          data-onboarding-next
          className="w-full sm:w-auto"
        >
          {t("onboarding.nav.next")}
        </Button>
      </div>
    </div>
  );
}
